/**
 * Reservation Detail Page Utilities
 * 예약 상세 페이지에서 사용하는 유틸리티 함수들
 */

import { format } from "date-fns";
import { zhTW } from "date-fns/locale";
import { getDateFnsLocale } from "@/lib/utils/date-fns-locale";

/**
 * "YYYY-MM-DD" date-only 문자열을 로컬 자정으로 파싱.
 *
 * new Date("2026-04-16")은 UTC 자정으로 해석되어, UTC보다 뒤처진 타임존에서
 * 로컬 날짜가 하루 전으로 밀리는 버그를 방지합니다.
 *
 * - YYYY-MM-DD  → 로컬 자정 Date
 * - UTC 자정 timestamp(예: "2026-12-08T00:00:00+00:00") → 로컬 자정 Date
 *   reservations.pickup_date/return_date 는 timestamptz 로 날짜를 UTC 자정에
 *   저장하므로, PostgREST 응답이 이 형태로 온다. 날짜 의미로 취급해야
 *   UTC−N 타임존 고객에게 하루 전 날짜가 표시되지 않는다.
 * - 그 외 형식  → 기본 new Date() 파싱 폴백 (실제 시각이 있는 timestamp 등)
 */
export function parseDateOnly(dateString: string): Date {
  const match = dateString.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ]00:00(?::00(?:\.0+)?)?(?:Z|[+-]00(?::?00)?))?$/,
  );
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return new Date(dateString);
}

/**
 * "YYYY-MM-DDTHH:mm[:ss]" 형식의 naive local datetime 문자열을 특정 타임존의
 * 실제 UTC 순간(Date)으로 변환합니다.
 *
 * 예: zonedTimeToUtc("2026-04-16T18:30", "Asia/Seoul") → 2026-04-16T09:30:00Z
 *
 * DST 전환 시각 부근에서 ±1시간 오차가 있을 수 있음.
 */
export function zonedTimeToUtc(localDateTime: string, timeZone: string): Date {
  const asIfUtc = new Date(
    localDateTime.endsWith("Z") ? localDateTime : `${localDateTime}Z`
  );
  if (Number.isNaN(asIfUtc.getTime())) return asIfUtc;
  const tzLocalStr = asIfUtc.toLocaleString("en-US", { timeZone, hour12: false });
  const utcLocalStr = asIfUtc.toLocaleString("en-US", { timeZone: "UTC", hour12: false });
  const offsetMs = new Date(tzLocalStr).getTime() - new Date(utcLocalStr).getTime();
  return new Date(asIfUtc.getTime() - offsetMs);
}

/**
 * 로케일에 따른 date-fns 로케일 객체 반환
 */
export function getDateLocale(locale: string) {
  // "zh" 는 앱 locale 이 아니지만 옛 호출부 호환으로 번체 유지.
  if (locale === "zh") return zhTW;
  return getDateFnsLocale(locale);
}

/**
 * 날짜 문자열을 로케일에 맞게 포맷팅
 */
export function formatReservationDate(
  dateString: string,
  locale: string,
): string {
  try {
    return format(parseDateOnly(dateString), "PPP", {
      locale: getDateLocale(locale),
    });
  } catch {
    return dateString;
  }
}

/**
 * 시간 문자열을 HH:mm 형식으로 포맷팅
 */
export function formatReservationTime(timeString: string | null): string {
  if (!timeString) return "";
  return timeString.substring(0, 5); // HH:mm
}

/**
 * 통화 코드에 따른 심볼 반환
 */
export function getCurrencySymbol(currency: string): string {
  switch (currency.toUpperCase()) {
    case "USD":
      return "$";
    case "KRW":
      return "₩";
    case "JPY":
      return "¥";
    case "CNY":
      return "¥";
    case "EUR":
      return "€";
    default:
      return currency;
  }
}

/**
 * 예약 상태에 따른 Tailwind 색상 클래스 반환
 */
export function getStatusColor(status: string): string {
  switch (status) {
    case "confirmed":
      return "bg-green-100 text-green-800";
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "in_use":
      return "bg-blue-100 text-blue-800";
    case "completed":
      return "bg-gray-100 text-gray-800";
    case "cancelled":
      return "bg-red-100 text-red-800";
    case "refunded":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

import type {
  PeriodOption,
  ReservationDuration,
  StatusBadgeVariant,
} from "@/types/reservation";

/**
 * 예약 상태를 탭 그룹으로 매핑
 */
export type StatusTab = "upcoming" | "inUse" | "completed" | "cancelled";

export function getStatusTab(status: string): StatusTab {
  switch (status) {
    case "pending":
    case "confirmed":
      return "upcoming";
    case "in_use":
      return "inUse";
    case "completed":
      return "completed";
    case "cancelled":
    case "refunded":
    case "refund_requested":
      return "cancelled";
    default:
      return "upcoming";
  }
}

export const STATUS_TABS: readonly StatusTab[] = [
  "upcoming",
  "inUse",
  "completed",
  "cancelled",
] as const;

/**
 * 기간 필터 옵션 상수
 */
export const PERIOD_FILTER_OPTIONS: readonly PeriodOption[] = [
  { value: "3months", labelKey: "period3Months", months: 3 },
  { value: "6months", labelKey: "period6Months", months: 6 },
  { value: "1year", labelKey: "period1Year", months: 12 },
  { value: "2years", labelKey: "period2Years", months: 24 },
] as const;

/**
 * 예약 상태에 따른 Badge variant 반환
 */
export function getStatusBadgeVariant(status: string): StatusBadgeVariant {
  switch (status) {
    case "confirmed":
    case "completed":
      return "secondary";
    case "pending":
    case "in_use":
      return "default";
    case "cancelled":
    case "refunded":
    case "refund_requested":
      return "destructive";
    default:
      return "outline";
  }
}

/**
 * 날짜를 요일 포함 형식으로 포맷 (예: "2026.01.24 (토)")
 */
export function formatDateWithWeekday(dateStr: string, locale: string): string {
  try {
    const date = parseDateOnly(dateStr);
    const dateLocale = getDateLocale(locale);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const weekday = format(date, "EEE", { locale: dateLocale });

    return `${year}.${month}.${day} (${weekday})`;
  } catch {
    return dateStr;
  }
}

/**
 * 날짜를 짧은 월/일 형식으로 포맷 (예: "1월 24일" 또는 "Jan 24")
 */
export function formatShortMonthDay(dateStr: string, locale: string): string {
  try {
    const date = parseDateOnly(dateStr);
    const dateLocale = getDateLocale(locale);
    return format(date, "MMM do", { locale: dateLocale });
  } catch {
    return dateStr;
  }
}

/**
 * 예약의 이용 기간 계산
 */
export function getReservationDuration(reservation: {
  rental_type?: string;
  timeslot_duration_hours?: number | null;
  pickup_date: string;
  return_date?: string | null;
}): ReservationDuration {
  if (
    reservation.rental_type === "timeslot" &&
    reservation.timeslot_duration_hours
  ) {
    return { type: "hours", value: reservation.timeslot_duration_hours };
  }

  if (reservation.return_date && reservation.pickup_date) {
    const pickup = parseDateOnly(reservation.pickup_date);
    const returnDate = parseDateOnly(reservation.return_date);
    const days =
      Math.ceil(
        (returnDate.getTime() - pickup.getTime()) / (1000 * 60 * 60 * 24),
      ) + 1;
    return { type: "days", value: days };
  }

  return { type: "days", value: 1 };
}

/**
 * 리뷰 작성 가능 기한 (이용 종료 후 N일).
 * 기준은 이용일(이용 종료시각). 90일로 넉넉히 잡아 완료/메일 지연이 있어도
 * 고객 작성 기회가 잠식되지 않도록 한다. (타임슬롯도 동일하게 이용일 기준)
 */
export const REVIEW_DEADLINE_DAYS = 90;

type ReviewEligibilityReservation = {
  status: string;
  rental_type?: string;
  timeslot_duration_hours?: number | null;
  pickup_date: string;
  pickup_time?: string | null;
  return_date?: string | null;
  return_time?: string | null;
  partner_timezone?: string | null;
};

function normalizeTime(t: string | null | undefined): string {
  if (!t) return "00:00:00";
  const parts = t.split(":");
  const hh = (parts[0] || "00").padStart(2, "0");
  const mm = (parts[1] || "00").padStart(2, "0");
  const ss = (parts[2] || "00").padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

/**
 * 예약의 이용 종료 시점(UTC)을 파트너 타임존 기준으로 계산
 *
 * - timeslot + duration: pickup 시각 + duration 시간
 * - daily (반납일 있음): 반납 시각 (없으면 23:59:59)
 * - 그 외: 픽업일 23:59:59
 */
export function getReservationEndTime(
  reservation: ReviewEligibilityReservation,
): Date {
  const partnerTimezone = reservation.partner_timezone || "Asia/Seoul";

  if (
    reservation.rental_type === "timeslot" &&
    reservation.timeslot_duration_hours
  ) {
    const pickupUtc = zonedTimeToUtc(
      `${reservation.pickup_date}T${normalizeTime(reservation.pickup_time)}`,
      partnerTimezone,
    );
    return new Date(
      pickupUtc.getTime() +
        reservation.timeslot_duration_hours * 60 * 60 * 1000,
    );
  }

  if (reservation.return_date) {
    const timeStr = reservation.return_time
      ? normalizeTime(reservation.return_time)
      : "23:59:59";
    return zonedTimeToUtc(
      `${reservation.return_date}T${timeStr}`,
      partnerTimezone,
    );
  }

  return zonedTimeToUtc(
    `${reservation.pickup_date}T23:59:59`,
    partnerTimezone,
  );
}

/**
 * 리뷰 작성 기한까지 남은 일수 반환.
 * 이용 종료 전이거나 기한(완료 시점 + REVIEW_DEADLINE_DAYS)이 지났으면 null.
 */
export function getReviewDaysRemaining(
  reservation: ReviewEligibilityReservation,
): number | null {
  const eligibleStatuses = ["confirmed", "in_use", "completed"];
  if (!eligibleStatuses.includes(reservation.status)) return null;
  if (reservation.rental_type === "option_only") return null;

  const endTime = getReservationEndTime(reservation);
  const now = new Date();

  if (endTime > now) return null;

  const deadlineMs =
    endTime.getTime() + REVIEW_DEADLINE_DAYS * 24 * 60 * 60 * 1000;
  const remainingMs = deadlineMs - now.getTime();
  if (remainingMs <= 0) return null;

  return Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
}

/**
 * 예약 카드/상세에서 리뷰 작성 버튼을 노출할지 결정.
 *
 * - option_only 렌탈은 리뷰 대상 아님
 * - development 환경: 항상 노출
 * - prod:
 *   - status === 'completed'
 *   - status === 'confirmed' | 'in_use' 이고 이용 종료 시점이 현재보다 과거
 */
export function canShowReviewButton(
  reservation: ReviewEligibilityReservation,
): boolean {
  if (reservation.rental_type === "option_only") return false;

  if (process.env.NODE_ENV === "development") return true;

  if (reservation.status === "completed") return true;

  if (
    reservation.status === "confirmed" ||
    reservation.status === "in_use"
  ) {
    return getReservationEndTime(reservation) <= new Date();
  }

  return false;
}
